import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Tag, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Layout from '../components/layout/Layout';
import { fashionShowEvent, applicationFormUrl } from '../data/mock';

const FashionShowEvent = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(fashionShowEvent.images[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % fashionShowEvent.images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(fashionShowEvent.images[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + fashionShowEvent.images.length) % fashionShowEvent.images.length;
    setCurrentIndex(newIndex);
    setSelectedImage(fashionShowEvent.images[newIndex]);
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 md:py-28">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${fashionShowEvent.images[0]})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <Link
            to="/news"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to News & Events
          </Link>
          <Badge className="bg-[#d4a855] text-[#142942] mb-4">
            {fashionShowEvent.category}
          </Badge>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
            {fashionShowEvent.title}
          </h1>
          <div className="flex flex-wrap gap-4 text-white/80">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#d4a855]" />
              {fashionShowEvent.date}
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#d4a855]" />
              {fashionShowEvent.location}
            </span>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Description */}
          <div className="max-w-4xl mx-auto mb-12">
            <p className="text-[#475569] text-lg leading-relaxed">
              {fashionShowEvent.description}
            </p>
          </div>

          {/* Photo Gallery */}
          <div className="mb-12">
            <h2 className="font-display text-2xl font-bold text-[#1e3a5f] mb-6 text-center">
              Event Gallery
            </h2>
            
            {/* Desktop Grid */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-4">
              {fashionShowEvent.images.map((image, index) => (
                <div
                  key={index}
                  className="relative aspect-square overflow-hidden rounded-xl cursor-pointer group"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={image}
                    alt={`Fashion Show ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[#1e3a5f]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              ))}
            </div>

            {/* Mobile Carousel */}
            <div className="md:hidden">
              <div className="relative">
                <div className="overflow-x-auto flex gap-4 pb-4 snap-x snap-mandatory scrollbar-hide">
                  {fashionShowEvent.images.map((image, index) => (
                    <div
                      key={index}
                      className="flex-shrink-0 w-[85%] snap-center"
                      onClick={() => openLightbox(index)}
                    >
                      <div className="relative aspect-square overflow-hidden rounded-xl">
                        <img
                          src={image}
                          alt={`Fashion Show ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-[#1e3a5f]/20" />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-center text-[#475569] text-sm mt-2">
                  Swipe to view more photos
                </p>
              </div>
            </div>
          </div>

          {/* Impact Line */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#1e3a5f] rounded-xl p-8 text-center">
              <p className="text-white text-lg md:text-xl font-medium italic">
                "{fashionShowEvent.impactLine}"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#f8fafc]">
        <div className="container-custom text-center">
          <h3 className="font-display text-xl md:text-2xl font-bold text-[#1e3a5f] mb-4">
            Want to Be Part of Our Community?
          </h3>
          <p className="text-[#475569] mb-6">
            Join BIT and participate in exciting events like this one.
          </p>
          <a href={applicationFormUrl} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white">
              Apply Now
            </Button>
          </a>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-[#d4a855] transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#d4a855] transition-colors p-2"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          
          <img
            src={selectedImage}
            alt="Fashion Show"
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
          />
          
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#d4a855] transition-colors p-2"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
            {currentIndex + 1} / {fashionShowEvent.images.length}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FashionShowEvent;
